// FarmersPulse — Topic Page Articles.
//
// Twelve evergreen introductory articles that map 1:1 to the tool
// tiles in `<WhatDoYouNeed />`. Each entry drives the shared
// `/tools/:slug` article page rendered by `FarmersToolArticle.jsx`.
//
// Editing rules:
//   • Copy comes from the publication-ready article set the product
//     owner supplied. Preserve wording, spelling and sentence rhythm.
//   • `infographic_url` stays empty until the owner uploads the 12
//     infographics — the page renders a "coming soon" placeholder
//     when the URL is falsy.
//   • `cta_to` points at the actual FarmersPulse surface that does
//     the work (weather feed, calendar, market prices, etc.).
//   • `related` slugs must resolve to keys in this same file.
//   • `modules` is a short list of the concrete UI blocks the tool
//     page should surface — used only for editorial reference right
//     now, wired later when the modules become real components.

import { IS_FARMERSPULSE } from "@/lib/site";

const BASE = IS_FARMERSPULSE ? "" : "/farmers";

/** @typedef {{ h: string, body: string[] }} Section */
/** @typedef {{
 *   slug: string, article_no: number, kicker: string, title: string,
 *   subtitle: string, intro: string[], sections: Section[],
 *   guidance: string, cta_label: string, cta_to: string,
 *   related: string[], modules: string,
 *   infographic_url?: string, infographic_alt?: string,
 * }} ToolArticle */

/** @type {ToolArticle[]} */
export const TOOL_ARTICLES = [
  {
    slug: "my-weather",
    article_no: 1,
    kicker: "Article 1 · Local Farm Weather",
    title: "My Weather",
    subtitle: "Weather information shaped around your farm.",
    intro: [
      "See current conditions, short-range forecasts, rainfall, wind, heat, frost risk and field-ready guidance for the location you choose.",
      "Farm weather is different from a general city forecast. A farmer needs to know not only whether rain is likely, but when it may begin, how much may fall, how strong the wind could become and what those conditions may mean for work in a particular field.",
      "My Weather brings the most useful local conditions into one practical view. Select your farm or current location to see available observations and forecasts. When farm records are connected, FarmersPulse can relate the weather to crops, irrigation history and upcoming tasks. Forecasts are guidance, not guarantees, so always combine them with field observations and official warnings.",
    ],
    sections: [
      {
        h: "What you can check",
        body: [
          "Current temperature and feels-like conditions",
          "Rain probability and estimated rainfall",
          "Wind speed, gusts and direction",
          "Humidity, dew point and evaporation conditions",
          "Heat, frost, storm and air-quality alerts when available",
          "Sunrise, sunset and useful field-work windows",
        ],
      },
      {
        h: "Use weather to plan farm work",
        body: [
          "Choose the farm, field or saved location you want to review.",
          "Look at hourly conditions before spraying, irrigating, harvesting or moving livestock.",
          "Compare expected rain with recent rainfall and soil moisture.",
          "Add important work to the Farm Calendar and request a reminder.",
          "Review official emergency alerts before acting during severe weather.",
        ],
      },
      {
        h: "Questions to ask FarmersPulse AI",
        body: [
          "When is the lowest-wind period for spraying tomorrow?",
          "How much rain has this field received this week?",
          "Could tonight's temperature threaten my crop?",
          "Which outdoor tasks should I move to another day?",
        ],
      },
    ],
    guidance: "Local sensors can differ from regional weather stations. Confirm critical decisions with on-farm measurements, product labels, extension guidance and official weather services.",
    cta_label: "Open My Weather",
    cta_to: `${BASE}/today#pulse-weather`,
    related: ["should-i-irrigate", "farm-calendar", "nearby-farm-alerts"],
    modules: "Location selector, current conditions, hourly outlook, rainfall history, alerts, saved farms",
    infographic_url: "https://customer-assets-cm19k8pv.emergentagent.net/job_53d4d8d8-0828-4dbf-a7c6-bb2f75105ed3/artifacts/1sy4vkqf_868038A3-E0D6-4112-BB79-FC86820C85FE.png",
    infographic_alt: "FarmersPulse infographic — My Weather · Local conditions for smarter farm decisions",
  },
  {
    slug: "what-should-i-plant",
    article_no: 2,
    kicker: "Article 2 · Crop Planning",
    title: "What Should I Plant",
    subtitle: "Find crops that fit your place, season and goals.",
    intro: [
      "Build a practical shortlist using location, planting window, growing conditions, available space, water, labor and intended market.",
      "The best crop is not simply the most popular crop. It must fit the farm's climate, soil, available water, labor, equipment, growing window and customers. What Should I Plant helps you compare those factors before you commit seed, land and time.",
      "Start with your location and expected planting period. Add what you know about the field, including soil texture, drainage, sunlight, irrigation access and past crops. Then choose your purpose: home use, farm stand, CSA, wholesale, livestock feed, processing or another market. FarmersPulse produces a shortlist with reasons, cautions and questions to investigate.",
    ],
    sections: [
      {
        h: "Information that improves the suggestion",
        body: [
          "Farm and field location",
          "Planting month and expected harvest window",
          "Soil type, pH and drainage when known",
          "Water availability and irrigation method",
          "Full sun, partial shade or protected growing",
          "Available labor, tools and storage",
          "Sales channel and customer demand",
          "Previous crops and rotation needs",
        ],
      },
      {
        h: "How to use the shortlist",
        body: [
          "Compare several suitable crops instead of relying on a single answer.",
          "Open each crop profile to review planting, spacing, water, nutrition and harvest guidance.",
          "Check seed availability, production costs and likely market before planting at scale.",
          "Start a small trial when the crop or variety is new to your farm.",
          "Add selected crops to My Crops and place key dates on the Farm Calendar.",
        ],
      },
      {
        h: "Good planning questions",
        body: [
          "Is this crop suitable for my frost-free period?",
          "Will it mature before expected heat, cold or heavy rain?",
          "Do I have enough labor during planting and harvest?",
          "Is there a buyer, CSA demand or local market for it?",
        ],
      },
    ],
    guidance: "Crop suggestions are educational planning support. Local extension services, seed suppliers and experienced growers can help confirm varieties and planting windows.",
    cta_label: "Build my crop shortlist",
    cta_to: `${BASE}/plan-what-to-plant`,
    related: ["my-crops", "market-prices", "farm-calendar"],
    modules: "Location and season form, field conditions, purpose selector, crop comparison, save to My Crops",
    infographic_url: "https://customer-assets-cm19k8pv.emergentagent.net/job_53d4d8d8-0828-4dbf-a7c6-bb2f75105ed3/artifacts/zj39ianx_965CB755-A475-4085-9755-6E0DB7A0AB82.png",
    infographic_alt: "FarmersPulse infographic — What Should I Plant · Choose crops that fit your place and season",
  },
  {
    slug: "should-i-irrigate",
    article_no: 3,
    kicker: "Article 3 · Irrigation Decision Support",
    title: "Should I Irrigate",
    subtitle: "Decide when water may be needed and how much to consider.",
    intro: [
      "Bring together recent rain, forecast conditions, crop stage, soil information, irrigation history and field observations.",
      "Irrigation decisions are rarely answered by the forecast alone. A field may receive rain but still have a dry root zone. Another field may look dry at the surface while holding adequate moisture below. Should I Irrigate organizes the available evidence so you can make a better field-level decision.",
      "Choose the field and crop, then add the most recent observation or soil-moisture reading. FarmersPulse compares that information with recorded irrigation, recent rainfall, expected weather and the crop's growth stage. The result should explain what supports watering, what supports waiting and what information is still missing.",
    ],
    sections: [
      {
        h: "Before you decide",
        body: [
          "Check soil moisture at more than one representative location.",
          "Confirm the crop and current growth stage.",
          "Review recent irrigation and rainfall rather than only today's weather.",
          "Look for runoff, ponding, crusting, wilting or uneven coverage.",
          "Check wind and heat before choosing the irrigation period.",
        ],
      },
      {
        h: "What the page should provide",
        body: [
          "A plain-language water-status summary",
          "The observations and records used",
          "A suggested next check or irrigation window",
          "An uncertainty statement when inputs are incomplete",
          "A place to record the decision and actual water applied",
        ],
      },
      {
        h: "Use water more effectively",
        body: [
          "Group fields by soil, crop and irrigation system.",
          "Inspect emitters, pressure, leaks and blocked lines regularly.",
          "Record runtime or applied depth after every irrigation.",
          "Compare results with plant response and later soil readings.",
          "Adjust future decisions using what happened in your own field.",
        ],
      },
    ],
    guidance: "This tool does not replace calibrated soil-moisture measurements, irrigation-system specifications, water regulations or professional agronomic advice.",
    cta_label: "Review my irrigation evidence",
    cta_to: `${BASE}/today#pulse-irrigation`,
    related: ["my-weather", "my-crops", "farm-calendar"],
    modules: "Field selector, moisture entry, rainfall and irrigation history, recommendation with uncertainty, record decision",
    infographic_url: "https://customer-assets-cm19k8pv.emergentagent.net/job_53d4d8d8-0828-4dbf-a7c6-bb2f75105ed3/artifacts/kqm0yrx7_E0716FAB-BF55-4FFD-B4B7-65207264DC6B.png",
    infographic_alt: "FarmersPulse infographic — Should I Irrigate · Use weather, soil and crop needs together",
  },
  {
    slug: "identify-a-pest",
    article_no: 4,
    kicker: "Article 4 · Pest Information",
    title: "Identify a Pest",
    subtitle: "Narrow down what you are seeing before you respond.",
    intro: [
      "Use photos, crop details, symptoms, location and timing to compare likely insects and other crop pests.",
      "A quick identification can be useful, but a wrong identification can waste money, delay treatment or harm beneficial organisms. Identify a Pest is designed to help you collect the right evidence and compare possible matches without pretending that a photograph alone provides certainty.",
      "Take clear photographs of the whole plant, the damaged area and the insect or organism from more than one angle. Add the crop, plant part, field location, date and pattern of damage. FarmersPulse can return possible matches, distinguishing characteristics, life-cycle information and next verification steps.",
    ],
    sections: [
      {
        h: "Photograph and describe the evidence",
        body: [
          "Use sharp images in natural light.",
          "Include a coin, ruler or familiar object for scale when helpful.",
          "Photograph the top and underside of leaves, stems, roots or fruit.",
          "Note whether damage appears on field edges, scattered plants or the whole field.",
          "Record recent weather, irrigation, fertilizer and pesticide activity.",
        ],
      },
      {
        h: "What a responsible result should show",
        body: [
          "Several possible matches when uncertainty exists",
          "Features that support or contradict each match",
          "Beneficial or harmless look-alikes",
          "Monitoring and nonchemical management options",
          "Links to locally appropriate extension or regulatory information",
          "A clear recommendation to seek expert confirmation when risk is high",
        ],
      },
      {
        h: "Before using a pesticide",
        body: [
          "Confirm the pest and determine whether action is necessary.",
          "Read and follow the complete product label.",
          "Check crop, pest, application rate, timing and re-entry restrictions.",
          "Protect pollinators, beneficial insects, water and neighboring properties.",
          "Record what was applied, where, when and why.",
        ],
      },
    ],
    guidance: "Image-based identification can be wrong. For regulated, invasive, toxic or economically serious pests, contact the appropriate extension, crop adviser or plant diagnostic service.",
    cta_label: "Compare pest matches",
    cta_to: `${BASE}/pest-heatmap`,
    related: ["analyze-plant-photo", "nearby-farm-alerts", "ask-farmerspulse-ai"],
    modules: "Photo uploader, crop and symptom form, possible matches, verification checklist, expert links",
    infographic_url: "https://customer-assets-cm19k8pv.emergentagent.net/job_53d4d8d8-0828-4dbf-a7c6-bb2f75105ed3/artifacts/at5gj5uy_71960E53-35C4-4879-975F-8C748C583FDE.png",
    infographic_alt: "FarmersPulse infographic — Identify a Pest · Recognize problems before they spread",
  },
  {
    slug: "analyze-plant-photo",
    article_no: 5,
    kicker: "Article 5 · Photo Analysis",
    title: "Analyze Plant Photo",
    subtitle: "Turn a plant photo into better questions and next steps.",
    intro: [
      "Analyze visible signs involving leaves, stems, fruit, weeds, pests, disease-like symptoms or soil conditions.",
      "A photograph can reveal color changes, spots, holes, distortion, wilting and other visible clues. It cannot show everything happening in the root zone, inside a stem or across an entire field. Analyze Plant Photo uses images as a starting point for investigation, not as a final diagnosis.",
      "Choose the subject type before uploading: plant or leaf, pest or insect, disease-like symptom, weed, fruit or soil. Add the crop or plant name if known, the location, the affected area and how quickly the condition developed. The analysis should explain what is visible, list reasonable possibilities and ask for missing evidence.",
    ],
    sections: [
      {
        h: "Take useful photographs",
        body: [
          "Begin with one image showing the whole plant and surroundings.",
          "Add a close image of the affected tissue.",
          "Photograph both healthy and affected plants for comparison.",
          "Use natural light and keep the subject in focus.",
          "Avoid filters, screenshots and heavily compressed images.",
          "Include roots, stem cross-sections or field patterns when relevant.",
        ],
      },
      {
        h: "What happens after upload",
        body: [
          "FarmersPulse describes visible features without overstating certainty.",
          "The assistant may ask about weather, crop stage, soil, irrigation or recent applications.",
          "Possible causes are grouped into pest, disease, nutrition, water, weather and physical injury.",
          "You receive suggested observations, tests or expert resources to confirm the cause.",
          "The photo and notes can be saved to the field record if you choose.",
        ],
      },
      {
        h: "High-risk situations",
        body: [
          "Quarantine or regulated pest concerns",
          "Rapidly spreading crop loss",
          "Possible pesticide injury or contamination",
          "Poisonous plant or food-safety concerns",
          "Symptoms affecting livestock or people",
        ],
      },
    ],
    guidance: "Do not rely on photo analysis alone for pesticide use, food-safety decisions, livestock exposure or regulated pest reporting.",
    cta_label: "Upload a plant photo",
    cta_to: `${BASE}/ask?mode=photo`,
    related: ["identify-a-pest", "my-crops", "report-a-farm-observation"],
    modules: "Subject chips, multi-photo uploader, follow-up questions, possible-cause groups, save observation",
    infographic_url: "https://customer-assets-cm19k8pv.emergentagent.net/job_53d4d8d8-0828-4dbf-a7c6-bb2f75105ed3/artifacts/1j7hbexx_000CF242-4444-424E-A4F1-4C4A122E843A.png",
    infographic_alt: "FarmersPulse infographic — Analyze Plant Photo · Turn visible symptoms into useful clues",
  },
  {
    slug: "my-crops",
    article_no: 6,
    kicker: "Article 6 · Crop Records",
    title: "My Crops",
    subtitle: "Keep every crop and field record in one working view.",
    intro: [
      "Track what was planted, where it is growing, what work was completed and what needs attention next.",
      "My Crops is the farmer's working record for active and past plantings. It connects each crop with its farm, field or growing area, variety, planting dates, inputs, observations, photographs, harvests, inventory and sales.",
      "Good records do more than document the past. They make it easier to compare varieties, remember what worked, schedule recurring work, explain a problem and calculate the cost of producing a crop. Start with simple information and add detail as it becomes useful.",
    ],
    sections: [
      {
        h: "Create a crop record",
        body: [
          "Choose the farm and field or growing area.",
          "Enter the crop, variety and intended market.",
          "Record seeding, transplanting or planting date.",
          "Add area, row length or plant count when known.",
          "Attach seed-lot details, photos and notes if useful.",
          "Set expected milestones and harvest dates.",
        ],
      },
      {
        h: "Keep the record useful",
        body: [
          "Log irrigation, fertilizer, pest management and field work.",
          "Add observations with date, location and photos.",
          "Record harvest quantity, grade, storage location and losses.",
          "Connect available inventory to Sell My Produce.",
          "Close the season with results and lessons for next year.",
        ],
      },
      {
        h: "Questions your records can answer",
        body: [
          "Which variety produced the best marketable yield?",
          "How much labor did this crop require?",
          "Which field had repeated pest or drainage problems?",
          "What was harvested and what remains available?",
          "Which planting dates performed best in this location?",
        ],
      },
    ],
    guidance: "FarmersPulse should let users control who can view private farm records and which information is shared publicly or with a buyer, adviser or group.",
    cta_label: "Open My Crops",
    cta_to: `${BASE}/mine`,
    related: ["farm-calendar", "sell-my-produce", "analyze-plant-photo"],
    modules: "Crop cards, field filters, activity timeline, records, harvest and inventory connection",
    infographic_url: "https://customer-assets-cm19k8pv.emergentagent.net/job_53d4d8d8-0828-4dbf-a7c6-bb2f75105ed3/artifacts/dtnuj5el_C3493C33-DC51-4AD5-9282-0BFEA5AC8FD4.png",
    infographic_alt: "FarmersPulse infographic — My Crops · Keep every crop record in one place",
  },
  {
    slug: "farm-calendar",
    article_no: 7,
    kicker: "Article 7 · Farm Schedule",
    title: "Farm Calendar",
    subtitle: "Put seasonal work, deadlines and reminders in one calendar.",
    intro: [
      "Plan crop, livestock, equipment, marketing and compliance tasks by farm, field, person and priority.",
      "Farm work is seasonal, interconnected and easy to lose among notebooks, messages and memory. The Farm Calendar turns plans and records into a schedule that can be viewed by day, week, month, crop, field or team member.",
      "Tasks may be entered manually or suggested from a crop plan, livestock routine, weather condition or saved template. Every suggestion remains editable. A farmer decides what belongs on the calendar, who receives it and when a reminder should be sent.",
    ],
    sections: [
      {
        h: "Useful calendar items",
        body: [
          "Planting, transplanting and harvest windows",
          "Irrigation, scouting and input applications",
          "Livestock feeding, breeding, health and movement",
          "Equipment maintenance and inspections",
          "Farmers market, CSA and delivery schedules",
          "Certification, permit, reporting and renewal dates",
          "Employee assignments and recurring routines",
        ],
      },
      {
        h: "Plan and record in the same place",
        body: [
          "Create the task with its farm, field or enterprise.",
          "Assign a person or group when appropriate.",
          "Set a time window, priority and reminder.",
          "Attach instructions, records, photos or weather conditions.",
          "Mark the task complete and record what actually happened.",
          "Reschedule unfinished work without losing its history.",
        ],
      },
      {
        h: "Weather-aware scheduling",
        body: [
          "Display expected rain, wind, heat or frost beside outdoor tasks.",
          "Warn when a planned activity may conflict with conditions or a product label.",
          "Offer alternative periods while leaving the decision with the farmer.",
        ],
      },
    ],
    guidance: "Calendar reminders support farm organization but do not replace legally required records, label instructions or official filing systems.",
    cta_label: "Open the Farm Calendar",
    cta_to: `${BASE}/calendar`,
    related: ["my-weather", "my-crops", "nearby-farm-alerts"],
    modules: "Calendar views, filters, task form, assignments, reminders, weather overlay",
    infographic_url: "https://customer-assets-cm19k8pv.emergentagent.net/job_53d4d8d8-0828-4dbf-a7c6-bb2f75105ed3/artifacts/abwdmpc8_8BF6A594-2346-4450-B8B5-6B7A9E12C2B0.png",
    infographic_alt: "FarmersPulse infographic — Farm Calendar · Stay ahead of every important farm task",
  },
  {
    slug: "market-prices",
    article_no: 8,
    kicker: "Article 8 · Market Information",
    title: "Market Prices",
    subtitle: "Compare available price information before you buy or sell.",
    intro: [
      "Review market reports, local listings and your own sales history with source, date, grade, unit and location kept visible.",
      "A price without context can be misleading. The same crop or livestock product may be quoted by pound, box, bushel, head or another unit. Grade, variety, organic status, packaging, volume, delivery terms and market location can all change the value.",
      "Market Prices should show where each figure came from and when it was reported. Farmers can filter by product, market, geography and time period, then compare public reports with local offers and their own completed sales.",
    ],
    sections: [
      {
        h: "Read every price carefully",
        body: [
          "Check the product, variety, class or grade.",
          "Confirm the unit and package size.",
          "Note the market and geographic location.",
          "Look at the report date and update frequency.",
          "Separate asking prices from completed transactions.",
          "Review volume, delivery, commission and payment terms.",
        ],
      },
      {
        h: "Use prices for planning",
        body: [
          "Estimate a range rather than one guaranteed selling price.",
          "Compare channels such as wholesale, direct retail, CSA and processing.",
          "Calculate packing, transport, market fees and expected losses.",
          "Save a target price or watchlist when available.",
          "Open Sell My Produce when you are ready to create a listing.",
        ],
      },
      {
        h: "What FarmersPulse should display",
        body: [
          "Source and last-updated time",
          "Original unit and normalized comparison unit",
          "High, low, average or range when supported",
          "Trend chart with enough history to be meaningful",
          "Clear notice when local data is limited or stale",
        ],
      },
    ],
    guidance: "Market information is educational and may be delayed, incomplete or not directly comparable. Buyers and sellers remain responsible for confirming current terms.",
    cta_label: "Compare market prices",
    cta_to: `${BASE}/markets`,
    related: ["sell-my-produce", "my-crops", "ask-farmerspulse-ai"],
    modules: "Product and location filters, source and update time, unit comparison, trend view, watchlist",
    infographic_url: "https://customer-assets-cm19k8pv.emergentagent.net/job_53d4d8d8-0828-4dbf-a7c6-bb2f75105ed3/artifacts/u0db12fk_C5BE5070-D538-40F5-9A6A-286E54483E22.png",
    infographic_alt: "FarmersPulse infographic — Market Prices · Know the market before you sell",
  },
  {
    slug: "sell-my-produce",
    article_no: 9,
    kicker: "Article 9 · Farm Sales",
    title: "Sell My Produce",
    subtitle: "Create a clear local listing for what your farm has available.",
    intro: [
      "Offer produce, livestock products, feed, plants and other farm goods with quantity, quality, location and fulfillment details.",
      "A useful farm listing answers the buyer's practical questions quickly. What is available? How much? Where is it located? When can it be picked up or delivered? How was it produced? What are the payment and minimum-order terms?",
      "Sell My Produce turns farm inventory into a shareable listing. You control whether it appears publicly, within a farmer group, to selected buyers or through connected local and topic pages in the Social Pulse network.",
    ],
    sections: [
      {
        h: "Build a complete listing",
        body: [
          "Use clear current photos of the actual product.",
          "Choose the product, variety, grade and production claim accurately.",
          "Enter quantity, unit, package size and minimum order.",
          "Set the price or invite verified inquiries when appropriate.",
          "Add availability dates, pickup, delivery and service area.",
          "State storage, handling or ordering instructions.",
          "Choose how buyers may contact you.",
        ],
      },
      {
        h: "Manage inquiries and availability",
        body: [
          "Route messages without publishing unnecessary personal information.",
          "Update quantities as orders are accepted.",
          "Pause or close the listing when inventory is no longer available.",
          "Record completed sales privately for farm analysis.",
          "Invite buyers to follow future availability or join a CSA list.",
        ],
      },
      {
        h: "Build buyer confidence",
        body: [
          "Keep descriptions accurate and avoid unsupported claims.",
          "Respond promptly and confirm order details in writing.",
          "Use safe payment and meeting practices.",
          "Follow applicable food, labeling, tax, licensing and transport rules.",
        ],
      },
    ],
    guidance: "FarmersPulse provides listing and communication tools. It should not imply inspection, certification, guaranteed payment or guaranteed product quality unless a specific verification is actually completed.",
    cta_label: "Create a listing",
    cta_to: `${BASE}/products`,
    related: ["market-prices", "my-crops", "nearby-farm-alerts"],
    modules: "Inventory picker, listing form, visibility settings, inquiry inbox, availability status",
    infographic_url: "https://customer-assets-cm19k8pv.emergentagent.net/job_53d4d8d8-0828-4dbf-a7c6-bb2f75105ed3/artifacts/vi9hsiwn_85F48332-405A-4E80-816A-6957A0E9D39E.png",
    infographic_alt: "FarmersPulse infographic — Sell My Produce · Reach more buyers with a clear farm listing",
  },
  {
    slug: "nearby-farm-alerts",
    article_no: 10,
    kicker: "Article 10 · Local Farm Awareness",
    title: "Nearby Farm Alerts",
    subtitle: "See timely farm conditions reported near the places you care about.",
    intro: [
      "Follow weather hazards, pest observations, animal concerns, road or market disruptions and other useful local notices.",
      "Farm conditions can change within a few miles. A hailstorm, pest emergence, smoke event, livestock escape, irrigation interruption or road closure may matter to nearby growers before it appears in a broad regional report.",
      "Nearby Farm Alerts combines available official information with community observations. Choose the farm, distance and topics you want to follow. Every alert should show its source, location precision, time and verification status so farmers can judge how much confidence to place in it.",
    ],
    sections: [
      {
        h: "Alerts you may follow",
        body: [
          "Severe weather, frost, heat, smoke and flooding",
          "Pest, disease-like symptom and invasive-species observations",
          "Water interruption, irrigation or local infrastructure issues",
          "Livestock, wildlife or biosecurity concerns",
          "Road closures, delivery disruptions and market changes",
          "Community requests, surplus opportunities and urgent farm needs",
        ],
      },
      {
        h: "Control what reaches you",
        body: [
          "Save one or more farm locations.",
          "Choose a radius or relevant administrative area.",
          "Select alert categories and urgency levels.",
          "Set push, email, text or in-app preferences when available.",
          "Mute repeated or irrelevant notices.",
          "Review the original source before taking consequential action.",
        ],
      },
      {
        h: "Contribute responsibly",
        body: [
          "Report what you directly observed.",
          "Provide time, approximate location, description and photo when safe.",
          "Protect private farm, home and personal information.",
          "Mark uncertainty and avoid naming a person or farm without evidence.",
          "Update or close the report when the condition changes.",
        ],
      },
    ],
    guidance: "Community reports may be incomplete or incorrect. Emergency and regulatory information should be confirmed through the responsible official authority.",
    cta_label: "Follow nearby alerts",
    cta_to: `${BASE}/community`,
    related: ["report-a-farm-observation", "my-weather", "identify-a-pest"],
    modules: "Map and list, radius and category filters, source and verification badges, notification settings",
    infographic_url: "https://customer-assets-cm19k8pv.emergentagent.net/job_53d4d8d8-0828-4dbf-a7c6-bb2f75105ed3/artifacts/7s68l5uz_E66D2B6E-1E4C-4F70-A85A-F2D5F683FD38.png",
    infographic_alt: "FarmersPulse infographic — Nearby Farm Alerts · Know what is happening around your farm",
  },
  {
    slug: "ask-farmerspulse-ai",
    article_no: 11,
    kicker: "Article 11 · Ask FarmersPulse",
    title: "Ask FarmersPulse AI",
    subtitle: "Ask a plain-language farming question.",
    intro: [
      "Receive an answer grounded in the information you choose to provide, including farm, soil, crop, water and observation records.",
      "Farmers do not always need another dashboard. Sometimes they need to ask a direct question: What should I check first? Why might these leaves be curling? When is the best available field-work window? Which records should I bring to an adviser?",
      "Ask FarmersPulse AI lets you write or speak naturally. You may attach a photograph and choose whether the question concerns a plant or leaf, pest or insect, disease-like symptom, weed, fruit, soil, livestock, equipment, weather, markets or another topic. The answer should separate what is known, what is inferred and what remains uncertain.",
    ],
    sections: [
      {
        h: "Ask a better question",
        body: [
          "Name the crop, animal, product or task.",
          "Include the farm or general location when relevant.",
          "Describe when the condition began and how it changed.",
          "Add measurements, records or clear photographs when available.",
          "Explain what you have already tried.",
          "State whether you need a quick checklist, detailed explanation or questions for an expert.",
        ],
      },
      {
        h: "What a trustworthy answer should do",
        body: [
          "Use your selected records only with your permission.",
          "Explain the reasoning in clear language.",
          "Identify missing information and uncertainty.",
          "Offer safe observations and next steps before high-risk action.",
          "Direct users to labels, official sources or qualified experts when required.",
          "Avoid presenting a photo-based guess as a confirmed diagnosis.",
        ],
      },
      {
        h: "Examples",
        body: [
          "What should I inspect after three days of heavy rain?",
          "Compare these two crop records and show what changed.",
          "Help me prepare questions for my extension agent.",
          "Is tomorrow's wind suitable for the task I scheduled?",
          "Turn today's field notes into a clear record.",
        ],
      },
    ],
    guidance: "AI can make mistakes. Do not rely on it alone for emergencies, pesticide decisions, veterinary care, human health, legal compliance, financial commitments or food-safety determinations.",
    cta_label: "Ask FarmersPulse AI",
    cta_to: `${BASE}/ask`,
    related: ["analyze-plant-photo", "my-crops", "farm-calendar"],
    modules: "Question box, listen and translate controls, photo analysis, record selector, answer with uncertainty",
    infographic_url: "https://customer-assets-cm19k8pv.emergentagent.net/job_53d4d8d8-0828-4dbf-a7c6-bb2f75105ed3/artifacts/dttmnd5f_9A3FED20-D391-4A51-A51A-B93D1751328B.png",
    infographic_alt: "FarmersPulse infographic — Ask FarmersPulse AI · Plain-language answers grounded in your farm",
  },
  {
    slug: "report-a-farm-observation",
    article_no: 12,
    kicker: "Article 12 · Community Report",
    title: "Report a Farm Observation",
    subtitle: "Share a useful local observation without overstating what you know.",
    intro: [
      "Report weather impacts, pests, crop conditions, water issues, wildlife, infrastructure or other farm events for the appropriate audience.",
      "A timely local observation can help neighboring farmers look more closely at their own fields. It can also document changing seasonal conditions over time. The value of a report depends on clear evidence, responsible wording and careful privacy choices.",
      "Report a Farm Observation guides the contributor through what happened, when it was seen, the approximate location, who may view it and whether an official authority has confirmed it. The report can remain private, be shared with a group or contribute to Nearby Farm Alerts when community sharing is selected.",
    ],
    sections: [
      {
        h: "What to include",
        body: [
          "Observation category and plain-language description",
          "Date and approximate time",
          "Farm, field or generalized map location",
          "Photos or video when safe and relevant",
          "Crop, animal, waterway or infrastructure involved",
          "The extent or pattern observed",
          "Actions already taken and current status",
          "Confidence level and official confirmation, if any",
        ],
      },
      {
        h: "Protect people and farms",
        body: [
          "Do not publish a person's private contact information.",
          "Generalize the location when exact coordinates could create risk.",
          "Avoid accusations, rumors and unsupported identification.",
          "Do not enter restricted areas or handle unknown pests or materials for a photo.",
          "Use emergency or regulatory reporting channels when the situation requires them.",
        ],
      },
      {
        h: "From observation to alert",
        body: [
          "FarmersPulse checks the requested visibility and location.",
          "Automated tools may flag duplicates, unsafe content or missing context.",
          "Moderators or trusted organizations may review high-impact reports.",
          "A shared report displays its source type, time and verification status.",
          "Corrections and updates remain connected to the original report.",
        ],
      },
    ],
    guidance: "This feature does not replace emergency services, veterinary authorities, plant-health agencies, environmental hotlines or other required official reporting.",
    cta_label: "Report an observation",
    cta_to: `${BASE}/report`,
    related: ["nearby-farm-alerts", "analyze-plant-photo", "ask-farmerspulse-ai"],
    modules: "Category form, location privacy, media upload, visibility, verification status, updates",
    infographic_url: "https://customer-assets-cm19k8pv.emergentagent.net/job_53d4d8d8-0828-4dbf-a7c6-bb2f75105ed3/artifacts/37yl0s9b_1AD0E928-6B7A-4198-8CDB-E322A872F5F4.png",
    infographic_alt: "FarmersPulse infographic — Report an Observation · Share useful field information with your community",
  },
];

export const TOOL_ARTICLE_BY_SLUG = Object.fromEntries(
  TOOL_ARTICLES.map((a) => [a.slug, a]),
);

// Convenience — used by <WhatDoYouNeed /> so the tile grid can point
// at the article page rather than jumping straight into the tool.
export const TOOL_ARTICLE_SLUGS = TOOL_ARTICLES.map((a) => a.slug);
